/**
 * Voice agents download: plain JSON request bodies.
 * Intentionally does not use `filterPayloadBuilder` / `buildDashboardFilterPayload`.
 */

/**
 * Strips `undefined` from a shallow object (e.g. filter snapshots).
 * Empty objects are preserved; empty string values are omitted.
 */
function stripObjectShallow(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined) continue;
    if (v === null) continue;
    if (v === "") continue;
    out[k] = v;
  }
  return out;
}

function stripFilterSnapshotFields(obj) {
  const s = stripObjectShallow(obj);
  const out = { ...s };
  for (const k of Object.keys(out)) {
    if (Array.isArray(out[k]) && out[k].length === 0) {
      delete out[k];
    }
  }
  return out;
}

/**
 * @param {string[]} parametersList
 * @param {object} listFilters
 * @param {object} advanced
 * @param {object} segment
 * @param {function} getAudioDurationValueForApi
 */
function buildCallFiltersSnapshot(
  { filters, advanced, segment, getAudioDurationValueForApi }
) {
  const f = {
    record_type: filters?.record_type || 'call',
    agent_id: filters?.agent,
    conversation_id: filters?.conversation_id,
    time: filters?.time,
    custom_start: filters?.customStart,
    custom_end: filters?.customEnd,
    mode: filters?.mode,
    audio_duration: getAudioDurationValueForApi ? getAudioDurationValueForApi() : undefined,
    conversation_direction: advanced?.conversation_direction,
    customer_id: advanced?.customer_id,
    disconnect_type: advanced?.disconnect_type,
    disconnect_reason: advanced?.disconnect_reason,
    average_latency: advanced?.average_latency,
    campaign_id: advanced?.campaign_id,
    campaign_batch_id: advanced?.campaign_batch_id,
    transfer_status: advanced?.transfer_status,
    transfer_agent_answered: advanced?.transfer_agent_answered,
    crm_update_status: advanced?.crm_update_status,
    // The calls backend expects the same JSON string and keys as the list endpoint.
    business_insights_multiple: JSON.stringify(advanced?.business_insights_multiple || []),
    agent_feedback_multiple: advanced?.agent_feedback_multiple || [],
    segment_type: segment?.type,
    segment_id: segment?.id,
    segment_name: segment?.name
  };
  return stripFilterSnapshotFields(f);
}

/**
 * @param {object} p
 * @param {string[]} p.parametersList
 * @param {string} p.filterType
 * @param {string[]} p.conversationIds
 * @param {string[]} p.customerIds
 * @param {object} p.filters
 * @param {object} p.advancedFilters
 * @param {object} p.segmentParameterData
 * @param {function} p.getAudioDurationValueForApi
 * @param {function} p.mapBusinessInsights
 * @param {number} p.totalRecords
 */
export function buildVoiceAgentCallsDownloadBody(p) {
  const {
    clientId,
    serviceId,
    email,
    parametersList = [],
    filterType = "quick",
    conversationIds = [],
    customerIds = [],
    totalRecords = 0,
    filters = {},
    advancedFilters = {},
    segmentParameterData = {},
    getAudioDurationValueForApi
  } = p;

  const body = {
    client_id: clientId,
    service_id: serviceId,
    email,
    menu: "conversations",
    page_number: 1,
    show_type: "downloads",
    page_size: totalRecords,
    perform_download: true,
    filter_type: filterType,
    conversation_ids: filterType === "conversation_ids" ? conversationIds : [],
    customer_ids: filterType === "customer_ids" ? customerIds : []
  };

  if (filterType === "quick") {
    body.filters = buildCallFiltersSnapshot({
      filters,
      advanced: advancedFilters,
      segment: segmentParameterData,
      getAudioDurationValueForApi
    });
  } else {
    body.filters = {};
  }

  const strParams = JSON.stringify(Array.isArray(parametersList) ? parametersList : []);
  return stripObjectShallow({
    ...body,
    parameters: strParams
  });
}

function buildCustomerFiltersSnapshot(
  { filters, advanced, segment, mapBusinessInsights }
) {
  const f = {
    agent_id: filters?.agent,
    customer_id: filters?.customer_id,
    first_interaction_time: filters?.firstinteraction,
    last_interaction_time: filters?.lastinteraction,
    first_interaction_custom_start: filters?.["First Interaction Start"],
    first_interaction_custom_end: filters?.["First Interaction End"],
    last_interaction_custom_start: filters?.["Last Interaction Start"],
    last_interaction_custom_end: filters?.["Last Interaction End"],
    interactions: advanced?.interactions,
    open_concerns: advanced?.openConcerns,
    customer_potential: advanced?.customerPotential,
    business_insights_multiple: mapBusinessInsights
      ? mapBusinessInsights(advanced?.business_insights_multiple || [])
      : advanced?.business_insights_multiple || [],
    concern_category: advanced?.concernCategory,
    concern_subcategory: advanced?.concernSubcategory,
    query_category: advanced?.queryCategory,
    query_subcategory: advanced?.querySubcategory,
    disposition: advanced?.disposition,
    subdisposition: advanced?.subdisposition,
    lead_source: advanced?.lead_source,
    tag: advanced?.tag,
    custom_module_parameter_name: advanced?.custom_module_parameter_name,
    custom_module_parameter_answer: advanced?.custom_module_parameter_answer,
    crm_lead_created_at: advanced?.crm_lead_created_at,
    crm_lead_created_at_start: advanced?.crm_lead_created_at_start,
    crm_lead_created_at_end: advanced?.crm_lead_created_at_end,
    segment_type: segment?.type,
    segment_id: segment?.id,
    segment_name: segment?.name
  };
  return stripFilterSnapshotFields(f);
}

/**
 * @param {object} p
 * @param {string[]} p.parametersList
 * @param {string} p.filterType
 * @param {string[]} p.customerIds
 * @param {object} p.filters
 * @param {object} p.advancedFilters
 * @param {object} p.segmentParameterData
 * @param {function} p.mapBusinessInsights
 * @param {number} p.totalRecords
 */
export function buildVoiceAgentCustomersDownloadBody(p) {
  const {
    clientId,
    serviceId,
    email,
    parametersList = [],
    filterType = "quick",
    customerIds = [],
    totalRecords = 0,
    filters = {},
    advancedFilters = {},
    segmentParameterData = {},
    mapBusinessInsights
  } = p;

  const body = {
    client_id: clientId,
    service_id: serviceId,
    email,
    menu: "customers",
    page_number: 1,
    show_type: "downloads",
    page_size: totalRecords,
    perform_download: true,
    filter_type: filterType,
    customer_ids: filterType === "customer_ids" ? customerIds : []
  };

  if (filterType === "quick") {
    body.filters = buildCustomerFiltersSnapshot({
      filters,
      advanced: advancedFilters,
      segment: segmentParameterData,
      mapBusinessInsights
    });
  } else {
    body.filters = {};
  }

  const strParams = JSON.stringify(Array.isArray(parametersList) ? parametersList : []);
  return stripObjectShallow({
    ...body,
    parameters: strParams
  });
}
